<template>
  <div class="row roomy align-center align-stretch">
    <div
      v-for="(sku, index) in filteredSkus"
      :key="sku"
      :class="[
        'column-12 size-sm',
        filteredSkus.length === 2 ? 'laptop:column-6' : 'laptop:column-4'
      ]"
    >
      <plan-summary-card
        v-if="!isLoading"
        :is-last="filteredSkus.length === index + 1"
        :is-single-plan-modal="filteredSkus.length === 1"
        :modal-context-slug="upsellTriggerSlug"
        :sku="getPlan(sku).sku"
        is-in-modal-context
        :subscription-discounts="subscriptionDiscounts"
      />
    </div>
  </div>
</template>

<script>
  import { get } from 'lodash'
  import { mapActions, mapGetters } from 'vuex'
  import { PLAN_COLOR_BY_SKU, getAutoAppliedDiscounts, getKickstarterDiscounts } from 'app/views/plans/PlansData'
  import PlanSummaryCard from 'app/views/plans/components/PlanSummaryCard'

  export default {
    name: 'UpsellOptions',

    components: {
      PlanSummaryCard
    },

    props: {
      icon: {
        type: Object
      },
      isCustomIcon: {
        type: Boolean,
        default: false
      },
      upsellTriggerSlug: {
        type: String,
        required: true
      },
      upsellTrigger: {
        type: String,
        default: null
      },
      requireProPlus: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        isLoading: true
      }
    },

    computed: {
      ...mapGetters('plans', ['skusForFilteredPlans', 'getPlan', 'subscriptionDiscounts']),
      ...mapGetters('subscriptions', ['currentSubscription']),

      hasActivePaidSubscription() {
        const status = get(this.currentSubscription, ['status'], null)
        const type = get(this.currentSubscription, ['type'], null)

        return !!this.currentSubscription && status === 'active' && type !== 'free'
      },

      userAction() {
        const trigger = String(this.upsellTrigger || '').toLowerCase()

        if (trigger.includes('downloading')) return 'proDownloadEnabled'
        if (trigger.includes('install')) return 'privateRegistriesEnabled'

        return 'active'
      },

      filteredSkus() {
        const normalizeSku = (rawSku) => {
          const sku = rawSku && typeof rawSku === 'object' && rawSku.sku ? rawSku.sku : rawSku
          return String(sku || '').toUpperCase()
        }

        const eligible = this.skusForFilteredPlans({
          icon: this.icon,
          userAction: this.userAction,
          subscription: this.$subscription,
          isCustomIcon: this.isCustomIcon,
          requireProPlus: this.requireProPlus
        }) || []

        const result = eligible
          .map(normalizeSku)
          // Ensure the SKU resolves to a real plan
          .filter((sku) => {
            const plan = this.getPlan(sku)
            return !!(plan && plan.sku)
          })
          // Paid subscribers: only show actionable upgrades in modals
          .filter((sku) => {
            if (!this.hasActivePaidSubscription) return true

            const currentSku = get(this.currentSubscription, ['plan', 'sku'], null)
            const isCurrent = currentSku && normalizeSku(currentSku) === normalizeSku(sku)

            if (isCurrent) return false
            if (this.isDowngradeSku(sku)) return false

            return true
          })

        // De-dupe while preserving order
        const unique = [...new Set(result)]

        // Keep the modal anchored to a small set
        return unique.slice(0, 3)
      },

      isBackerWithoutSubscription() {
        if (!this.$account) {
          return false
        }

        return this.$account.isBacker && !this.$subscription
      }
    },

    watch: {
      filteredSkus: {
        immediate: true,
        handler(val) {
          const count = Array.isArray(val) ? val.length : 0
          this.$emit('onSkuCount', count)
        }
      }
    },

    async created() {
      await this.fetchPlans()

      this.$emit('onPlansReady', true)

      if (this.globalDiscountCode) {
        await this.fetchDiscounts([this.globalDiscountCode])
      } else if (getKickstarterDiscounts().length > 0 && this.isBackerWithoutSubscription) {
        await this.fetchDiscounts(getKickstarterDiscounts())
      } else if (getAutoAppliedDiscounts().length > 0) {
        await this.fetchDiscounts(getAutoAppliedDiscounts())
      }

      this.isLoading = false
    },

    methods: {
      ...mapActions('plans', ['fetchPlans', 'fetchDiscounts']),

      isDowngradeSku(targetSku) {
        const currentPlan = get(this.currentSubscription, ['plan'], null)
        const currentSubscriptionStatus = get(this.currentSubscription, ['status'], null)

        if (!currentPlan || !targetSku) return false

        const sku = String(targetSku).toUpperCase()

        // If a subscription is canceled, the allowed list differs.
        if (currentSubscriptionStatus === 'canceled') {
          return !get(currentPlan, ['allowedCanceledPurchases'], []).includes(sku)
        }

        return !get(currentPlan, ['allowedUpgrades'], []).includes(sku)
      },

      getPlanIconStyle(sku) {
        return sku.toLowerCase().includes('-plus') ? 'custom' : 'solid'
      },

      planColor(sku) {
        return get(PLAN_COLOR_BY_SKU, [sku], 'teal')
      }
    }
  }
</script>
